import { useEffect } from 'react';

/**
 * Reveal-on-scroll for any element with the `reveal` class inside the
 * marketing root. Marks the element with `data-in` once it enters the
 * viewport, then stops observing it.
 *
 * The marker is an attribute rather than a class on purpose: React owns
 * `className`, so a class added here is wiped the next time the element
 * re-renders with a different className (e.g. an accordion opening),
 * which would fade revealed content back out. React never touches
 * `data-in`, so the reveal survives re-renders.
 *
 * A MutationObserver picks up `.reveal` elements added after mount.
 * Without it, anything rendered from a fetch — the blog list, the notes
 * rail — is never observed, and since `.reveal` starts at `opacity: 0`
 * it stays invisible while remaining laid out and clickable. That is
 * only visible on a cold cache, because a warm React Query cache
 * renders the content on the same tick as the mount effect.
 *
 * `key` (the current pathname) restarts the observers after client-side
 * navigation between marketing pages.
 */
export function useReveal(rootRef: React.RefObject<HTMLElement>, key: string): void {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.setAttribute('data-in', '');
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -10% 0px' },
    );

    const observe = (el: Element): void => {
      if (!el.hasAttribute('data-in')) io.observe(el);
    };

    root.querySelectorAll<HTMLElement>('.reveal').forEach(observe);

    const mo = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.classList.contains('reveal')) observe(node);
          node.querySelectorAll<HTMLElement>('.reveal').forEach(observe);
        }
      }
    });
    mo.observe(root, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [rootRef, key]);
}
